import { z } from 'zod';
import { IsoDate } from './primitives.js';

/**
 * The Household OS graph (Wave 1, Household Identity): typed household
 * members and the relationships between them. `attributes` is validated per
 * kind with a strict object per kind — an unknown field for the kind (a
 * `breed` on a vehicle, a `plate` on a child) is a 400, not silent data.
 */

export const HouseholdMemberKind = z.enum([
  'parent',
  'child',
  'pet',
  'staff',
  'guest',
  'vehicle',
  'property',
  'room',
]);
export type HouseholdMemberKind = z.infer<typeof HouseholdMemberKind>;

export const HouseholdRelationshipKind = z.enum([
  'parent_of',
  'owner_of',
  'assigned_to',
  'attends',
  'responsible_for',
  'lives_in',
  'related_to',
]);
export type HouseholdRelationshipKind = z.infer<typeof HouseholdRelationshipKind>;

const ShortText = z.string().min(1).max(200);

export const PersonAttributes = z
  .object({
    phone: z.string().min(1).max(40).optional(),
    email: z.string().email().max(254).optional(),
    birth_date: IsoDate.optional(),
  })
  .strict();
export type PersonAttributes = z.infer<typeof PersonAttributes>;

export const ChildAttributes = z
  .object({
    birth_date: IsoDate.optional(),
    school: ShortText.optional(),
    // Vaccination dates the child pack turns into calendar reminders.
    vaccinations: z
      .array(z.object({ name: ShortText, date: IsoDate }).strict())
      .max(50)
      .optional(),
  })
  .strict();
export type ChildAttributes = z.infer<typeof ChildAttributes>;

const ClockTime = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/);

export const PetAttributes = z
  .object({
    species: ShortText.optional(),
    breed: ShortText.optional(),
    birth_date: IsoDate.optional(),
    food_brand: ShortText.optional(),
    food_stock_days: z.number().int().min(0).max(3650).optional(),
    // When the current stock was bought — the dog pack's food detector
    // counts down from this + food_stock_days.
    food_last_bought: IsoDate.optional(),
    vet: ShortText.optional(),
    // Wall-clock "HH:MM" entries; the dog pack builds the feeding rrule
    // from them.
    feeding_times: z.array(ClockTime).min(1).max(6).optional(),
    last_vaccination: IsoDate.optional(),
    next_vaccination: IsoDate.optional(),
  })
  .strict();
export type PetAttributes = z.infer<typeof PetAttributes>;

export const VehicleAttributes = z
  .object({
    plate: z.string().min(1).max(20).optional(),
    make: ShortText.optional(),
    model: ShortText.optional(),
    insurance_expiry: IsoDate.optional(),
    registration_expiry: IsoDate.optional(),
    service_interval_km: z.number().int().min(0).optional(),
    // The vehicle pack's service reminder counts 6 months from this.
    service_last_date: IsoDate.optional(),
    // An odometer reading — its presence tells the vehicle pack the owner
    // tracks the car, so the fuel-log card is worth offering.
    service_last_km: z.number().int().min(0).optional(),
  })
  .strict();
export type VehicleAttributes = z.infer<typeof VehicleAttributes>;

export const PropertyAttributes = z
  .object({
    address: z.string().min(1).max(500).optional(),
    type: z.string().min(1).max(100).optional(),
    inspection_cycle_days: z.number().int().min(1).optional(),
    // The property pack's rhythm cards count from these; the defaults
    // (semiannual AC, quarterly pest control) apply when unset.
    ac_service_cycle_days: z.number().int().min(1).optional(),
    pest_control_cycle_days: z.number().int().min(1).optional(),
    // The inspection countdown's anchor; the member's created_at is the
    // fallback when no inspection has been recorded yet.
    last_inspection: IsoDate.optional(),
  })
  .strict();
export type PropertyAttributes = z.infer<typeof PropertyAttributes>;

/**
 * Staff are people (phone/email/birth_date) plus the documents a UAE
 * household must stay ahead of — the staff pack turns these into calendar
 * reminders (visa 60 days out, passport 90, contract review 30).
 */
export const StaffAttributes = PersonAttributes.extend({
  visa_expiry: IsoDate.optional(),
  passport_expiry: IsoDate.optional(),
  contract_end: IsoDate.optional(),
}).strict();
export type StaffAttributes = z.infer<typeof StaffAttributes>;

export const RoomAttributes = z
  .object({
    label: ShortText.optional(),
    floor: z.string().min(1).max(50).optional(),
  })
  .strict();
export type RoomAttributes = z.infer<typeof RoomAttributes>;

/** The attributes schema for a kind — the single place a new kind is wired in. */
export const HouseholdMemberAttributesForKind = {
  parent: PersonAttributes,
  child: ChildAttributes,
  pet: PetAttributes,
  staff: StaffAttributes,
  guest: PersonAttributes,
  vehicle: VehicleAttributes,
  property: PropertyAttributes,
  room: RoomAttributes,
} as const;

const MemberName = z.string().min(1).max(200);

export const CreateHouseholdMemberRequest = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('parent'),
    name: MemberName,
    attributes: PersonAttributes.default({}),
  }),
  z.object({ kind: z.literal('child'), name: MemberName, attributes: ChildAttributes.default({}) }),
  z.object({ kind: z.literal('pet'), name: MemberName, attributes: PetAttributes.default({}) }),
  z.object({
    kind: z.literal('staff'),
    name: MemberName,
    attributes: StaffAttributes.default({}),
  }),
  z.object({
    kind: z.literal('guest'),
    name: MemberName,
    attributes: PersonAttributes.default({}),
  }),
  z.object({
    kind: z.literal('vehicle'),
    name: MemberName,
    attributes: VehicleAttributes.default({}),
  }),
  z.object({
    kind: z.literal('property'),
    name: MemberName,
    attributes: PropertyAttributes.default({}),
  }),
  z.object({ kind: z.literal('room'), name: MemberName, attributes: RoomAttributes.default({}) }),
]);
export type CreateHouseholdMemberRequest = z.infer<typeof CreateHouseholdMemberRequest>;

/**
 * PATCH body. `attributes` is deliberately unshaped here: which fields are
 * legal depends on the member's kind, and the kind is only known once the row
 * is read — the handler re-validates against
 * `HouseholdMemberAttributesForKind[member.kind]` so an unknown field for the
 * kind is still a 400. A provided `attributes` REPLACES the bag wholesale
 * (no deep merge): the client sends the complete object.
 */
export const UpdateHouseholdMemberRequest = z
  .object({
    name: MemberName.optional(),
    attributes: z.record(z.unknown()).optional(),
    archived: z.boolean().optional(),
  })
  .strict();
export type UpdateHouseholdMemberRequest = z.infer<typeof UpdateHouseholdMemberRequest>;

export const CreateHouseholdRelationshipRequest = z
  .object({
    to_member_id: z.string().min(1).max(40),
    kind: HouseholdRelationshipKind,
  })
  .strict();
export type CreateHouseholdRelationshipRequest = z.infer<typeof CreateHouseholdRelationshipRequest>;

export const HouseholdMember = z.object({
  id: z.string(),
  kind: HouseholdMemberKind,
  name: z.string(),
  attributes: z.record(z.unknown()),
  user_id: z.string().nullable(),
  archived_at: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type HouseholdMember = z.infer<typeof HouseholdMember>;

export const HouseholdMemberRelationship = z.object({
  id: z.string(),
  from_member_id: z.string(),
  to_member_id: z.string(),
  kind: HouseholdRelationshipKind,
  created_at: z.string(),
});
export type HouseholdMemberRelationship = z.infer<typeof HouseholdMemberRelationship>;
